/**
 * The store — catalogue, product detail, cart, checkout and order tracking.
 *
 * The commerce decision this file embodies is recorded in TECHNICAL_DOCUMENT.md §2: the
 * home page carries a four-product strip and nothing more, and the shop lives here at
 * its own address with the filters, the pagination and the depth. A landing page that
 * tries to be a catalogue converts on neither.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ORDER_STATUS_LABEL, addMoney, friendlyError, multiplyMoney, percentOf, type OrderStatus } from '@serendib/shared';
import { api } from '../../lib/api.js';
import { useCart, toast } from '../../lib/stores.js';
import { setting, useBootstrap, useCartQuote, useProduct, useShopFilters, useShopProducts } from '../../lib/queries.js';
import { EmptyState, Field, Money, Pagination, Skeleton, StockPill } from '../../components/ui.jsx';
import { ProductCardView } from '../../components/cards.jsx';
import { ProductArtwork } from '../../components/Artwork.jsx';
import { Markdown } from '../../lib/markdown.jsx';
import { usePageSeo, NotFound } from './ContentPages.jsx';
import { trackAddToCart, trackInitiateCheckout, trackPurchase, trackViewContent } from '../../lib/tracking.js';
import { Breadcrumbs, HOME_CRUMB } from '../../components/Breadcrumbs.jsx';
import { itemListLd, productLd } from '../../lib/seo.js';

/* ========================================================================== */
/* Shop                                                                       */
/* ========================================================================== */

export function Shop() {
  const [params, setParams] = useSearchParams();
  const { data: filters } = useShopFilters();

  const query = {
    page: Number(params.get('page') ?? 1),
    pageSize: 12,
    search: params.get('search') ?? undefined,
    category: params.get('category') ?? undefined,
    brand: params.get('brand') ?? undefined,
    inStockOnly: params.get('inStockOnly') ?? undefined,
    sort: params.get('sort') ?? 'featured',
  };

  const { data, isLoading, isPlaceholderData } = useShopProducts(query);
  const activeCategory = filters?.categories.find((c) => c.slug === query.category);

  usePageSeo({
    // The category name leads the title when one is selected, because that is the
    // phrase people search — "barcode scanners Sri Lanka", not "hardware store".
    title: activeCategory ? `${activeCategory.name} — hardware store` : 'Hardware store',
    description: activeCategory
      ? `${activeCategory.name} in stock in Sri Lanka, supplied and installed by Serendib IT Solutions.`
      : 'Commercial-grade point-of-sale hardware in stock in Sri Lanka — terminals, thermal printers, barcode scanners, cash drawers and accessories.',
    // Every filtered and paged view collapses onto the bare shop, or onto the category
    // it belongs to. Without this each combination is a near-duplicate competing with
    // the page it was filtered from.
    canonicalPath: activeCategory ? `/shop?category=${activeCategory.slug}` : '/shop',
    noIndex: Boolean(query.search) || query.page > 1,
    crumbs: activeCategory
      ? [HOME_CRUMB, { name: 'Store', path: '/shop' }, { name: activeCategory.name, path: `/shop?category=${activeCategory.slug}` }]
      : [HOME_CRUMB, { name: 'Store', path: '/shop' }],
    jsonLd: data?.items.length
      ? [itemListLd(data.items.map((pr) => ({ name: pr.name, path: `/shop/${pr.slug}` })))]
      : [],
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const hasFilters = Boolean(query.search || query.category || query.brand || query.inStockOnly);
  const activeFilterCount = [query.search, query.category, query.brand, query.inStockOnly].filter(Boolean).length;

  // Open on a wide screen, where the rail is a sidebar and the CSS ignores this; closed
  // on a narrow one, where it is a disclosure sitting above the results.
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <p className="eyebrow">Hardware store</p>
        <h1 style={{ marginTop: 12 }}>Counter hardware, in stock in Colombo.</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>
          We import it ourselves, set it up with our software, and the same team installs it for you.
        </p>
      </div>

      <div className="shop-layout">
        {/* -- Filter rail ------------------------------------------------- */}
        <aside className="panel shop-rail" data-open={filtersOpen}>
          <button
            type="button"
            className="shop-filters-toggle"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            <span>
              Filters
              {activeFilterCount > 0 ? (
                <span className="pill pill--accent" style={{ marginLeft: 8 }}>{activeFilterCount}</span>
              ) : null}
            </span>
            <span aria-hidden="true">{filtersOpen ? '−' : '+'}</span>
          </button>

          <form
            onSubmit={(e) => { e.preventDefault(); setParam('search', new FormData(e.currentTarget).get('search') as string); }}
            className="stack"
            style={{ gap: 8 }}
          >
            <label className="label" htmlFor="shop-search">Search</label>
            <input id="shop-search" className="input" name="search" defaultValue={query.search ?? ''} placeholder="Printer, scanner, item code…" />
          </form>

          <div className="filter-group" style={{ marginTop: 22 }}>
            <p className="label" style={{ marginBottom: 8 }}>Category</p>
            <button className="filter-option" aria-pressed={!query.category} onClick={() => setParam('category', null)}>
              <span>All categories</span>
              <span className="filter-option__count">
                {filters?.categories.reduce((n, c) => n + c.product_count, 0) ?? ''}
              </span>
            </button>
            {filters?.categories.map((category) => (
              <button
                key={category.id}
                className="filter-option"
                aria-pressed={query.category === category.slug}
                onClick={() => setParam('category', category.slug)}
              >
                <span>{category.name}</span>
                <span className="filter-option__count">{category.product_count}</span>
              </button>
            ))}
          </div>

          {/*
            Two brands is the point at which a brand filter chooses anything. With one,
            the dropdown is a control that cannot change the result — so it is hidden.
          */}
          {filters && filters.brands.length > 1 ? (
            <div className="filter-group">
              <p className="label" style={{ marginBottom: 8 }}>Brand</p>
              <select className="select" value={query.brand ?? ''} onChange={(e) => setParam('brand', e.target.value || null)}>
                {/* `Field · any` as the no-filter option, everywhere, rather than a blank. */}
                <option value="">Brand · any</option>
                {filters.brands.map((brand) => <option key={brand.id} value={brand.slug}>{brand.name}</option>)}
              </select>
            </div>
          ) : null}

          <div className="filter-group">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={query.inStockOnly === 'true'}
                onChange={(e) => setParam('inStockOnly', e.target.checked ? 'true' : null)}
              />
              <span className="small">Hide items out of stock</span>
            </label>

            {hasFilters ? (
              <button className="btn btn--secondary btn--sm btn--block" style={{ marginTop: 10 }} onClick={() => setParams(new URLSearchParams())}>
                Clear all filters
              </button>
            ) : null}
          </div>
        </aside>

        {/* -- Results ----------------------------------------------------- */}
        <div className="panel">
          <div className="spread" style={{ marginBottom: 22 }}>
            <p className="small dim">
              {/* The count waits for the answer — never "0 products" over a request
                  that has not returned. */}
              {isLoading ? 'Loading…' : `${data?.total ?? 0} product${data?.total === 1 ? '' : 's'}`}
            </p>
            <select className="select" style={{ width: 'auto' }} value={query.sort} onChange={(e) => setParam('sort', e.target.value)}>
              <option value="featured">Sort · featured</option>
              <option value="newest">Newest first</option>
              <option value="price-asc">Price, low to high</option>
              <option value="price-desc">Price, high to low</option>
              <option value="name-asc">Name, A–Z</option>
            </select>
          </div>

          {isLoading ? (
            <div className="grid grid--3 grid--cards">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={330} radius={22} />)}</div>
          ) : !data || data.items.length === 0 ? (
            <EmptyState
              title="Nothing found"
              reason={hasFilters ? 'Nothing matches all the filters you chose.' : 'There are no products yet.'}
              action={hasFilters ? <button className="btn btn--primary" onClick={() => setParams(new URLSearchParams())}>Clear filters</button> : undefined}
            />
          ) : (
            <>
              <div className="grid grid--3 grid--cards" style={{ opacity: isPlaceholderData ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                {data.items.map((product) => <ProductCardView key={product.id} product={product} />)}
              </div>
              <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={(n) => setParam('page', String(n))} filtered={hasFilters} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Product detail                                                             */
/* ========================================================================== */

export function ProductDetail() {
  const { slug = '' } = useParams();
  const { data: product, isLoading, isError, error } = useProduct(slug);
  const add = useCart((s) => s.add);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [tab, setTab] = useState<'specs' | 'description'>('specs');

  /**
   * ViewContent, once per product.
   *
   * Keyed on the id rather than the object: the query refetches on focus and window
   * changes and hands back a NEW object with identical contents, which would re-fire
   * the event and inflate the denominator of every product's view-to-purchase rate.
   */
  useEffect(() => {
    if (!product) return;
    trackViewContent(
      { productId: product.id, name: product.name, quantity: 1, unitPrice: product.price },
      product.currency,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  usePageSeo({
    title: product?.seoTitle ?? product?.name,
    description: product?.seoDescription ?? product?.shortDescription,
    image: product?.imageUrl,
    type: 'product',
    canonicalPath: `/shop/${slug}`,
    crumbs: product
      ? [
          HOME_CRUMB,
          { name: 'Store', path: '/shop' },
          ...(product.categorySlug && product.categoryName
            ? [{ name: product.categoryName, path: `/shop?category=${product.categorySlug}` }]
            : []),
          { name: product.name, path: `/shop/${slug}` },
        ]
      : [],
    jsonLd: product
      ? [
          productLd({
            name: product.name,
            description: product.shortDescription,
            sku: product.sku,
            brand: product.brandName,
            image: product.imageUrl,
            price: product.price,
            currency: product.currency,
            path: `/shop/${slug}`,
            inStock: product.available > 0,
            allowBackorder: product.allowBackorder,
          }),
        ]
      : [],
  });

  if (isLoading) {
    return (
      <div className="wrap section stack" style={{ gap: 20 }}>
        <Skeleton height={520} radius={34} />
      </div>
    );
  }
  if (isError && (error as { status?: number })?.status === 404) return <NotFound what="product" />;
  if (!product) return <NotFound what="product" />;

  const sellable = product.available > 0 || product.allowBackorder;
  const images = product.images.length > 0 ? product.images : product.imageUrl ? [{ id: 0, url: product.imageUrl, alt: product.imageAlt }] : [];

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <Breadcrumbs
        trail={[
          HOME_CRUMB,
          { name: 'Store', path: '/shop' },
          ...(product.categorySlug && product.categoryName
            ? [{ name: product.categoryName, path: `/shop?category=${product.categorySlug}` }]
            : []),
          { name: product.name, path: `/shop/${slug}` },
        ]}
      />

      <div className="panel">
        <div className="grid product-grid">
          {/* -- Gallery --------------------------------------------------- */}
          <div className="stack" style={{ gap: 12 }}>
            <div style={{ aspectRatio: '1 / 1', background: 'var(--panel-2)', borderRadius: 'var(--r-card)', overflow: 'hidden' }}>
              {images[activeImage] ? (
                <img src={images[activeImage]!.url} alt={images[activeImage]!.alt ?? product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <ProductArtwork category={product.categorySlug} name={product.name} sku={product.sku} />
              )}
            </div>

            {images.length > 1 ? (
              <div className="row" style={{ gap: 10 }}>
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setActiveImage(index)}
                    aria-label={`View image ${index + 1}`}
                    aria-current={index === activeImage}
                    style={{
                      width: 76, height: 76, padding: 0, borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                      background: 'var(--panel-2)',
                      border: index === activeImage ? '2px solid var(--accent-deep)' : '1px solid var(--hairline)',
                    }}
                  >
                    <img src={image.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* -- Buy box --------------------------------------------------- */}
          <div className="stack" style={{ gap: 18 }}>
            {product.brandName ? (
              <p className="eyebrow" style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>{product.brandName}</p>
            ) : null}

            <h1 style={{ fontSize: 'var(--t-h1)' }}>{product.name}</h1>

            <p className="xs dim">
              {product.model ? `Model: ${product.model} · ` : ''}Item code: {product.sku}
            </p>

            <div className="row" style={{ gap: 14 }}>
              <p style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em' }}>
                <Money value={product.price} currency={product.currency} decimals={false} />
              </p>
              {product.compareAtPrice ? (
                <p className="dim" style={{ textDecoration: 'line-through' }}>
                  <Money value={product.compareAtPrice} currency={product.currency} decimals={false} />
                </p>
              ) : null}
              <StockPill state={product.stockState} available={product.available} />
            </div>

            {product.shortDescription ? <p className="lead">{product.shortDescription}</p> : null}

            {product.highlights?.length ? (
              <div>
                <p className="label" style={{ marginBottom: 10 }}>Main points</p>
                <ul className="stack" style={{ gap: 8 }}>
                  {product.highlights.map((highlight) => (
                    <li key={highlight} className="row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                      <span aria-hidden="true" style={{ color: 'var(--accent-deep)', fontWeight: 700 }}>•</span>
                      <span className="small muted">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="row" style={{ gap: 12, marginTop: 6 }}>
              <div className="row" style={{ gap: 0, border: '1px solid var(--hairline)', borderRadius: 'var(--r-pill)', overflow: 'hidden', flexWrap: 'nowrap' }}>
                <button className="btn btn--ghost" onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" style={{ borderRadius: 0, padding: '0 16px' }}>−</button>
                <span className="mono" style={{ minWidth: 44, textAlign: 'center', fontSize: 15 }} aria-live="polite">{quantity}</span>
                <button className="btn btn--ghost" onClick={() => setQuantity((q) => Math.min(999, q + 1))} aria-label="Increase quantity" style={{ borderRadius: 0, padding: '0 16px' }}>+</button>
              </div>

              <button
                className="btn btn--primary"
                style={{ flex: 1, minWidth: 200 }}
                disabled={!sellable}
                onClick={() => {
                  add({ productId: product.id, quantity, name: product.name, slug: product.slug, unitPrice: product.price, imageUrl: product.imageUrl });
                  trackAddToCart({ productId: product.id, name: product.name, quantity, unitPrice: product.price }, product.currency);
                  toast.success(`${quantity} × ${product.name} added to your cart.`);
                }}
              >
                {sellable ? 'Add to cart' : 'Out of stock'}
              </button>
            </div>

            {!sellable ? (
              <p className="hint">
                Out of stock right now. <Link to="/contact">Ask us when it will come</Link> — we import it ourselves and can usually give you a date.
              </p>
            ) : null}

            {product.warrantyText ? <p className="hint">{product.warrantyText}</p> : null}
          </div>
        </div>
      </div>

      {/* -- Specs / description --------------------------------------------- */}
      {product.specs.length > 0 || product.description ? (
        <div className="panel">
          <div className="row" role="tablist" style={{ gap: 6, marginBottom: 22 }}>
            {product.specs.length > 0 ? (
              <button role="tab" aria-selected={tab === 'specs'} className={`btn btn--sm ${tab === 'specs' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setTab('specs')}>
                Technical specifications
              </button>
            ) : null}
            {product.description ? (
              <button role="tab" aria-selected={tab === 'description'} className={`btn btn--sm ${tab === 'description' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setTab('description')}>
                Description &amp; support
              </button>
            ) : null}
          </div>

          {tab === 'specs' && product.specs.length > 0 ? (
            <div className="table-scroll">
              <table className="table">
                <tbody>
                  {product.specs.map((spec) => (
                    <tr key={spec.label}>
                      <th scope="row" style={{ width: '32%', position: 'static', background: 'transparent', color: 'var(--ink)', fontSize: 'var(--t-sm)' }}>{spec.label}</th>
                      <td className="muted">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {tab === 'description' && product.description ? <Markdown>{product.description}</Markdown> : null}
        </div>
      ) : null}

      {product.related?.length ? (
        <div className="panel">
          <h2 style={{ fontSize: 'var(--t-h3)' }}>Goes well with this</h2>
          <div className="grid grid--4 grid--cards" style={{ marginTop: 22 }}>
            {product.related.map((related) => <ProductCardView key={related.id} product={related} />)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ========================================================================== */
/* Cart                                                                       */
/* ========================================================================== */

export function Cart() {
  const { lines, setQuantity, remove } = useCart();
  const navigate = useNavigate();
  usePageSeo({ title: 'Your cart', noIndex: true });

  const cartLines = useMemo(() => lines.map((l) => ({ productId: l.productId, quantity: l.quantity })), [lines]);
  const { data: quote, isLoading } = useCartQuote(cartLines);

  if (lines.length === 0) {
    return (
      <div className="wrap section">
        <div className="panel">
          <EmptyState
            title="Your cart is empty"
            reason="Nothing has been added yet."
            action={<Link className="btn btn--primary" to="/shop">Go to the store</Link>}
          />
        </div>
      </div>
    );
  }

  // Priced from the SERVER's quote, not from the stored snapshot. A cart left open for
  // a week would otherwise display last month's price right up to the checkout.
  const priced = quote?.lines ?? [];
  const subtotal = priced.length > 0 ? addMoney(...priced.map((l) => multiplyMoney(l.unitPrice, l.quantity))) : '0.00';
  const unavailable = new Map((quote?.unavailable ?? []).map((u) => [u.productId, u.reason]));

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <h1>Your cart</h1>
        <p className="lead" style={{ marginTop: 10 }}>
          {lines.length} item{lines.length === 1 ? '' : 's'} in your cart.
        </p>
      </div>

      <div className="grid checkout-grid">
        <div className="panel stack" style={{ gap: 4 }}>
          {lines.map((line) => {
            const server = priced.find((p) => p.productId === line.productId);
            const problem = unavailable.get(line.productId);
            return (
              <div key={line.productId} className="row" style={{ gap: 16, padding: '16px 0', borderBottom: '1px solid var(--hairline)', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                <Link to={`/shop/${line.slug}`} style={{ width: 84, height: 84, flex: 'none', borderRadius: 12, overflow: 'hidden', background: 'var(--panel-2)' }}>
                  {/* The generated illustration when there is no photograph, the same as
                      the catalogue uses. Without it this was a bare grey square — the
                      one place in the whole checkout where the customer is asked to
                      confirm what they are buying, showing nothing. */}
                  {line.imageUrl ? (
                    <img src={line.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <ProductArtwork category={server?.categorySlug} name={line.name} sku={server?.sku ?? line.slug} />
                  )}
                </Link>

                <div className="stack" style={{ gap: 6, flex: 1, minWidth: 0 }}>
                  <Link to={`/shop/${line.slug}`} style={{ color: 'var(--ink)', fontWeight: 600 }}>{line.name}</Link>
                  <p className="xs dim">{server?.sku ?? ''}</p>

                  {problem === 'ERR_PRODUCT_UNAVAILABLE' ? (
                    <p className="field-error">We no longer sell this. Remove it to continue.</p>
                  ) : problem === 'ERR_INSUFFICIENT_STOCK' ? (
                    <p className="field-error">Only {server?.available ?? 0} left. Lower the quantity to continue.</p>
                  ) : null}

                  <div className="row" style={{ gap: 10, marginTop: 4 }}>
                    <div className="row" style={{ gap: 0, border: '1px solid var(--hairline)', borderRadius: 'var(--r-pill)', overflow: 'hidden', flexWrap: 'nowrap' }}>
                      <button className="btn btn--ghost btn--sm" style={{ borderRadius: 0 }} onClick={() => setQuantity(line.productId, line.quantity - 1)} aria-label="Decrease">−</button>
                      <span className="mono small" style={{ minWidth: 36, textAlign: 'center' }}>{line.quantity}</span>
                      <button className="btn btn--ghost btn--sm" style={{ borderRadius: 0 }} onClick={() => setQuantity(line.productId, line.quantity + 1)} aria-label="Increase">+</button>
                    </div>
                    <button className="btn btn--ghost btn--sm" onClick={() => remove(line.productId)}>Remove</button>
                  </div>
                </div>

                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <p style={{ fontWeight: 700 }}>
                    <Money value={multiplyMoney(server?.unitPrice ?? line.unitPrice, line.quantity)} currency={quote?.currency ?? 'LKR'} />
                  </p>
                  {server && server.unitPrice !== line.unitPrice ? (
                    <p className="xs" style={{ color: 'var(--warn)' }}>Price has changed</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <OrderSummary
          subtotal={subtotal}
          quote={quote}
          isLoading={isLoading}
          blocked={unavailable.size > 0}
          action={
            <button className="btn btn--primary btn--block" disabled={unavailable.size > 0 || isLoading} onClick={() => navigate('/checkout')}>
              Continue to checkout
            </button>
          }
        />
      </div>
    </div>
  );
}

/**
 * The totals panel, shared by the cart and the checkout.
 *
 * Fulfilment is passed in because delivery is free on collection, and showing a
 * delivery charge on the cart that then disappears at checkout reads as a mistake even
 * though it is correct.
 */
function OrderSummary({
  subtotal,
  quote,
  isLoading,
  blocked,
  action,
  fulfilment = 'DELIVERY',
}: {
  subtotal: string;
  quote: { taxRate: number; shippingFlat: string; freeShippingOver: string | null; currency: string } | undefined;
  isLoading: boolean;
  blocked?: boolean;
  action: React.ReactNode;
  fulfilment?: 'DELIVERY' | 'PICKUP';
}) {
  const { data: bootstrap } = useBootstrap();
  const taxLabel = setting(bootstrap, 'store.tax_label', 'VAT');
  const note = setting(bootstrap, 'store.checkout_note', '');

  const currency = quote?.currency ?? 'LKR';
  const taxRate = quote?.taxRate ?? 0;

  const shipping =
    fulfilment === 'PICKUP'
      ? '0.00'
      : quote?.freeShippingOver && Number(subtotal) >= Number(quote.freeShippingOver)
        ? '0.00'
        : (quote?.shippingFlat ?? '0.00');

  const taxable = addMoney(subtotal, shipping);
  const tax = percentOf(taxable, taxRate);
  const total = addMoney(taxable, tax);

  return (
    <div className="panel stack" style={{ gap: 14, position: 'sticky', top: 100 }}>
      <h2 style={{ fontSize: 'var(--t-h3)' }}>Order summary</h2>

      {isLoading ? (
        <Skeleton height={120} />
      ) : (
        <div className="stack" style={{ gap: 9 }}>
          <div className="spread"><span className="small muted">Items total</span><span className="small"><Money value={subtotal} currency={currency} /></span></div>
          <div className="spread">
            <span className="small muted">{fulfilment === 'PICKUP' ? 'Collection' : 'Delivery'}</span>
            <span className="small">{Number(shipping) === 0 ? 'Free' : <Money value={shipping} currency={currency} />}</span>
          </div>
          {taxRate > 0 ? (
            <div className="spread"><span className="small muted">{taxLabel} ({taxRate}%)</span><span className="small"><Money value={tax} currency={currency} /></span></div>
          ) : null}
          <div className="spread" style={{ paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
            <span style={{ fontWeight: 600 }}>Total</span>
            <span style={{ fontWeight: 700, fontSize: 20 }}><Money value={total} currency={currency} /></span>
          </div>
        </div>
      )}

      {blocked ? <p className="field-error">Please fix the marked items before you continue.</p> : null}
      {action}
      {note ? <p className="hint">{note}</p> : null}
    </div>
  );
}

/* ========================================================================== */
/* Checkout                                                                   */
/* ========================================================================== */

export function Checkout() {
  const { lines, clear } = useCart();
  const navigate = useNavigate();
  const [fulfilment, setFulfilment] = useState<'DELIVERY' | 'PICKUP'>('DELIVERY');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: bootstrap } = useBootstrap();
  usePageSeo({ title: 'Checkout', noIndex: true });

  const cartLines = useMemo(() => lines.map((l) => ({ productId: l.productId, quantity: l.quantity })), [lines]);
  const { data: quote, isLoading } = useCartQuote(cartLines);

  const priced = quote?.lines ?? [];
  const subtotal = priced.length > 0 ? addMoney(...priced.map((l) => multiplyMoney(l.unitPrice, l.quantity))) : '0.00';

  /**
   * InitiateCheckout, once per visit to this screen.
   *
   * Guarded by a ref rather than an empty dependency list: `lines` changes whenever a
   * quantity is edited on this page, and an effect keyed on it would report a fresh
   * checkout for every keystroke.
   */
  const checkoutReported = useRef(false);
  useEffect(() => {
    if (checkoutReported.current || lines.length === 0) return;
    checkoutReported.current = true;
    trackInitiateCheckout(
      lines.map((l) => ({ productId: l.productId, name: l.name, quantity: l.quantity, unitPrice: l.unitPrice })),
      setting(bootstrap, 'store.currency', 'LKR'),
    );
  }, [lines, bootstrap]);

  if (lines.length === 0) {
    return (
      <div className="wrap section">
        <div className="panel">
          <EmptyState title="Your cart is empty" reason="There is nothing to check out." action={<Link className="btn btn--primary" to="/shop">Go to the store</Link>} />
        </div>
      </div>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    try {
      const result = await api.post<{ orderNumber: string; total: string; currency: string }>(
        '/checkout',
        {
          lines: cartLines,
          customerName: String(form.get('customerName') ?? ''),
          customerEmail: String(form.get('customerEmail') ?? ''),
          customerPhone: String(form.get('customerPhone') ?? ''),
          companyName: form.get('companyName') ? String(form.get('companyName')) : null,
          fulfilment,
          paymentMethod: String(form.get('paymentMethod') ?? 'BANK_TRANSFER'),
          addressLine1: form.get('addressLine1') ? String(form.get('addressLine1')) : null,
          addressLine2: form.get('addressLine2') ? String(form.get('addressLine2')) : null,
          city: form.get('city') ? String(form.get('city')) : null,
          postalCode: form.get('postalCode') ? String(form.get('postalCode')) : null,
          notes: form.get('notes') ? String(form.get('notes')) : null,
        },
        { auth: false },
      );

      // Reported BEFORE the cart is emptied — `clear()` is what the next line does,
      // and reading the lines after it would send an order with nothing in it.
      trackPurchase({
        orderNumber: result.orderNumber,
        total: result.total,
        currency: result.currency,
        items: lines.map((l) => ({ productId: l.productId, name: l.name, quantity: l.quantity, unitPrice: l.unitPrice })),
      });

      // Cleared only AFTER the server confirms. Clearing optimistically and then
      // failing loses the customer's whole basket for a network blip.
      clear();
      navigate(`/order/${result.orderNumber}`, { state: { total: result.total, currency: result.currency }, replace: true });
    } catch (err) {
      setSubmitting(false);
      setError(friendlyError(err));
      const errors = (err as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (errors) setFieldErrors(errors);
    }
  }

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <h1>Checkout</h1>
        <p className="lead" style={{ marginTop: 10 }}>
          Fill in your details below. It takes a minute.
        </p>
      </div>

      <form onSubmit={submit} className="grid checkout-grid" noValidate>
        <div className="panel stack" style={{ gap: 22 }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="label" style={{ marginBottom: 12 }}>Your details</legend>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <Field label="Full name" required error={fieldErrors.customerName}>
                <input className="input" name="customerName" required autoComplete="name" />
              </Field>
              <Field label="Email" required error={fieldErrors.customerEmail}>
                <input className="input" name="customerEmail" type="email" required autoComplete="email" />
              </Field>
              <Field label="Phone number" required error={fieldErrors.customerPhone}>
                <input className="input" name="customerPhone" type="tel" required autoComplete="tel" placeholder="011 330 40 48" />
              </Field>
              <Field label="Company" error={fieldErrors.companyName}>
                <input className="input" name="companyName" autoComplete="organization" />
              </Field>
            </div>
          </fieldset>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="label" style={{ marginBottom: 12 }}>How would you like it?</legend>
            <div className="row" style={{ gap: 8 }}>
              {(['DELIVERY', 'PICKUP'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`btn btn--sm ${fulfilment === mode ? 'btn--primary' : 'btn--secondary'}`}
                  aria-pressed={fulfilment === mode}
                  onClick={() => setFulfilment(mode)}
                >
                  {mode === 'DELIVERY' ? 'Deliver to me' : 'I will collect'}
                </button>
              ))}
            </div>

            {fulfilment === 'DELIVERY' ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
                <Field label="Address" required error={fieldErrors.addressLine1}>
                  <input className="input" name="addressLine1" required autoComplete="address-line1" />
                </Field>
                <Field label="Address line 2" error={fieldErrors.addressLine2}>
                  <input className="input" name="addressLine2" autoComplete="address-line2" />
                </Field>
                <Field label="City" required error={fieldErrors.city}>
                  <input className="input" name="city" required autoComplete="address-level2" />
                </Field>
                <Field label="Postal code" error={fieldErrors.postalCode}>
                  <input className="input" name="postalCode" autoComplete="postal-code" />
                </Field>
              </div>
            ) : (
              <p className="hint" style={{ marginTop: 14 }}>
                Collect from {setting(bootstrap, 'store.pickup_address', 'our Homagama office')}. We will call you when it is ready.
              </p>
            )}
          </fieldset>

          <Field label="How would you like to pay?">
            <select className="select" name="paymentMethod" defaultValue="BANK_TRANSFER">
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="CASH_ON_DELIVERY">Cash on delivery</option>
              <option value="INVOICE_ME">Send my company a bill</option>
            </select>
          </Field>

          <Field label="Anything we should know?" error={fieldErrors.notes}>
            <textarea className="textarea" name="notes" rows={3} placeholder="Delivery instructions, any fitting you need, or a date you need it by…" />
          </Field>

          {error ? <p className="field-error" role="alert">{error}</p> : null}
        </div>

        <OrderSummary
          subtotal={subtotal}
          quote={quote}
          isLoading={isLoading}
          fulfilment={fulfilment}
          action={
            <button className="btn btn--primary btn--block" type="submit" disabled={submitting || isLoading}>
              {submitting ? 'Placing order…' : 'Place order'}
            </button>
          }
        />
      </form>
    </div>
  );
}

/* ========================================================================== */
/* Confirmation and tracking                                                  */
/* ========================================================================== */

export function OrderPlaced() {
  const { orderNumber = '' } = useParams();
  usePageSeo({ title: 'Order placed', noIndex: true });

  return (
    <div className="wrap section">
      <div className="panel" style={{ textAlign: 'center', display: 'grid', gap: 16, justifyItems: 'center' }}>
        <span aria-hidden="true" style={{ width: 56, height: 56, borderRadius: 999, background: 'var(--accent-wash)', color: 'var(--accent-deep)', display: 'grid', placeItems: 'center', fontSize: 26 }}>✓</span>
        <h1>Order placed</h1>
        <p className="lead measure">
          Your order number is <strong className="mono">{orderNumber}</strong>. We have kept the stock for you and
          will call you soon to confirm and arrange payment.
        </p>
        <p className="small dim">Keep the order number. You need it, with your email address, to check your order.</p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Link className="btn btn--primary" to="/track">Check this order</Link>
          <Link className="btn btn--secondary" to="/shop">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}

export function TrackOrder() {
  const [result, setResult] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  usePageSeo({
    title: 'Check your order',
    description: 'Check the progress of an order placed with Serendib IT Solutions.',
    // Indexable — people search for it — but it carries no content worth ranking
    // beyond the form itself.
    crumbs: [HOME_CRUMB, { name: 'Check your order', path: '/track' }],
  });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    const form = new FormData(event.currentTarget);
    try {
      setResult(
        await api.get(
          `/public/orders/track?orderNumber=${encodeURIComponent(String(form.get('orderNumber')))}&email=${encodeURIComponent(String(form.get('email')))}`,
          { auth: false },
        ),
      );
    } catch (err) {
      // A wrong number and a wrong email give the SAME message. Distinguishing them
      // would turn this into a way to test whether an order number exists.
      setError((err as { status?: number }).status === 404
        ? 'We could not find an order with that number and email address together.'
        : friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <h1>Check your order</h1>
        <p className="lead measure" style={{ marginTop: 12 }}>
          Enter your order number and the email address you used. We ask for both so nobody else can see your order.
        </p>

        <form onSubmit={submit} className="row" style={{ gap: 12, marginTop: 24, alignItems: 'flex-end' }}>
          <Field label="Order number" required><input className="input" name="orderNumber" required placeholder="SIS-001000" style={{ minWidth: 180 }} /></Field>
          <Field label="Email address" required><input className="input" name="email" type="email" required style={{ minWidth: 220 }} /></Field>
          <button className="btn btn--primary" type="submit" disabled={loading}>{loading ? 'Looking…' : 'Find my order'}</button>
        </form>

        {error ? <p className="field-error" style={{ marginTop: 16 }} role="alert">{error}</p> : null}
      </div>

      {result ? (
        <div className="panel">
          <div className="spread">
            <div>
              <p className="eyebrow">{result.orderNumber}</p>
              <h2 style={{ fontSize: 'var(--t-h3)', marginTop: 6 }}>{result.customerName}</h2>
            </div>
            <span className="pill pill--accent">{ORDER_STATUS_LABEL[result.status as OrderStatus] ?? result.status}</span>
          </div>

          <div className="table-scroll" style={{ marginTop: 24 }}>
            <table className="table">
              <thead><tr><th>Item</th><th className="hide-sm">Item code</th><th className="num">Qty</th><th className="num">Total</th></tr></thead>
              <tbody>
                {(result.items ?? []).map((item: Record<string, any>) => (
                  <tr key={item.sku}>
                    <td>{item.name}</td>
                    <td className="hide-sm mono xs">{item.sku}</td>
                    <td className="num">{item.quantity}</td>
                    <td className="num"><Money value={item.lineTotal} currency={result.currency} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="spread" style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hairline)' }}>
            <span style={{ fontWeight: 600 }}>Order total</span>
            <span style={{ fontWeight: 700, fontSize: 19 }}><Money value={result.total} currency={result.currency} /></span>
          </div>

          {(result.events ?? []).length > 0 ? (
            <div style={{ marginTop: 26 }}>
              <p className="label" style={{ marginBottom: 12 }}>Progress</p>
              <ol className="stack" style={{ gap: 10 }}>
                {result.events.map((event: Record<string, any>, index: number) => (
                  <li key={index} className="row" style={{ gap: 12 }}>
                    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--accent)' }} />
                    <span className="small">{ORDER_STATUS_LABEL[event.toStatus as OrderStatus] ?? event.toStatus}</span>
                    <span className="xs dim">{new Date(event.createdAt).toLocaleString('en-GB')}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
